import PostCheckoutQuery from '../../services/query/postCheckout/PostCheckoutQuery.service';
import CutOffQuery from '../../services/query/cutOff/CutOffQuery.services';
import CargoManagementQuery from '../../services/query/cargoManagement/CargoManagementQuery.services';

export type CutOffFilterOptionNeeds = {
    needsCargoCompanies: boolean;
    needsDistanceRanges: boolean;
    needsDeliveryTypes: boolean;
    needsDepartureOptions: boolean;
    needsArrivalOptions: boolean;
};

export type CutOffFilterOptionSetters = {
    setCargoCompanyOptions: (options: { id: number; name: string }[]) => void;
    setDistanceOptions: (options: { id: number; name: string }[]) => void;
    setDeliveryTypeOptions: (options: any[]) => void;
    setDepartureLocationOptions: (options: { id: number; name: string }[]) => void;
    setArrivalLocationOptions: (options: { id: number; name: string }[]) => void;
    setAddressTypeId?: (id: number) => void;
};

export const resolveWarehouseList = (response: any): any[] => {
    if (Array.isArray(response)) return response;
    const candidates = [
        response?.warehouses,
        response?.data?.warehouses,
        response?.data?.warehouseList,
        response?.data?.items,
        response?.items,
        response?.data,
    ];
    return candidates.find(Array.isArray) ?? [];
};

const mapShippingCompanies = (shippingRes: any) => {
    if (!shippingRes?.shippingCompanies || !Array.isArray(shippingRes.shippingCompanies)) {
        return [];
    }
    return shippingRes.shippingCompanies
        .filter((company: any) => company?.shippingCompanyId != null && company?.name != null)
        .map((company: any) => ({
            id: company.shippingCompanyId,
            name: company.name ?? '',
        }));
};

const mapDepartureOptions = (warehouseRes: any) => {
    return resolveWarehouseList(warehouseRes).map((item: any) => ({
        id: item?.locationId ?? item?.id,
        name: item?.warehouseName ?? item?.name ?? '',
    }));
};

const mapArrivalOptions = (locationRes: any) => {
    if (!locationRes?.data?.addressEntities || !Array.isArray(locationRes.data.addressEntities)) {
        return [];
    }
    return locationRes.data.addressEntities.map((item: any) => ({
        id: item?.addressEntityId ?? item?.id,
        name: item?.name ?? '',
    }));
};

const buildApiCalls = (needs: CutOffFilterOptionNeeds) => {
    const apiCalls: { key: string; call: Promise<any> }[] = [];
    if (needs.needsCargoCompanies) {
        apiCalls.push({ key: 'cargoCompanies', call: PostCheckoutQuery.GetShippingCompanies() });
    }
    if (needs.needsDistanceRanges) {
        apiCalls.push({ key: 'distanceRanges', call: CutOffQuery.GetDistanceRanges() });
    }
    if (needs.needsDeliveryTypes) {
        apiCalls.push({ key: 'deliveryTypes', call: CutOffQuery.GetAllDeliveryType() });
    }
    if (needs.needsDepartureOptions) {
        apiCalls.push({ key: 'departureLocations', call: CutOffQuery.GetWarehousesByCountry() });
    }
    if (needs.needsArrivalOptions) {
        apiCalls.push({
            key: 'arrivalAddressType',
            call: CutOffQuery.GetCountryCutOffAddressEntityTypeByRuleBFF(),
        });
    }
    return apiCalls;
};

export const loadCutOffFilterOptions = async (
    needs: CutOffFilterOptionNeeds,
    setters: CutOffFilterOptionSetters
): Promise<void> => {
    const apiCalls = buildApiCalls(needs);
    if (apiCalls.length === 0) {
        return;
    }

    const responses = await Promise.all(apiCalls.map((item) => item.call));
    const responseMap = new Map<string, any>();
    apiCalls.forEach((item, index) => responseMap.set(item.key, responses[index]));

    if (needs.needsCargoCompanies) {
        setters.setCargoCompanyOptions(mapShippingCompanies(responseMap.get('cargoCompanies')));
    }

    if (needs.needsDistanceRanges) {
        const distanceRes = responseMap.get('distanceRanges');
        if (Array.isArray(distanceRes?.distanceRanges)) {
            setters.setDistanceOptions(distanceRes.distanceRanges ?? []);
        }
    }

    if (needs.needsDeliveryTypes) {
        const deliveryTypeRes = responseMap.get('deliveryTypes');
        if (Array.isArray(deliveryTypeRes?.deliveryTypes)) {
            setters.setDeliveryTypeOptions(deliveryTypeRes.deliveryTypes);
        }
    }

    if (needs.needsDepartureOptions) {
        setters.setDepartureLocationOptions(mapDepartureOptions(responseMap.get('departureLocations')));
    }

    if (needs.needsArrivalOptions) {
        const addressTypeRes = responseMap.get('arrivalAddressType');
        const addressEntityTypeId =
            addressTypeRes?.countryCuttoffAddressEntityType ?? addressTypeRes?.countryCuttoffAddressEntityType;

        if (addressEntityTypeId) {
            setters.setAddressTypeId?.(addressEntityTypeId);
            try {
                const locationRes = await CargoManagementQuery.SubAddressEntities({
                    AddressEntityTypeId: addressEntityTypeId,
                });
                setters.setArrivalLocationOptions(mapArrivalOptions(locationRes));
            } catch (error_) {
                console.error('Error fetching location options:', error_);
            }
        }
    }
};

export const excludeAllOptionId = (value: any[], allOptionId: number) => value.filter((id) => id !== allOptionId);
